/**
 * Layers: shared ES module (assets/layers.js).
 *
 * Loaded only when settings.layers_enabled is on (snippets/layers-bootstrap.liquid). Boots
 * @commerce-blocks/sdk@3.0.0 once (tokens from the app embed's #layers-config script), then sets
 * window.LayersTheme and dispatches `layers:ready` on document. Surface modules wait for it:
 *
 *   const LT = await (window.LayersTheme?.ready || new Promise((r) =>
 *     document.addEventListener('layers:ready', (e) => r(e.detail), { once: true })));
 *
 * window.LayersTheme
 *   .ready                          Promise<LayersTheme>, resolved once booted
 *   .sdk()                          SDK instance (createSDK data), or null without app-embed tokens
 *   .config                         window.LayersConfig (snippets/layers-config.liquid)
 *   .alpine.mount(root)             initTree on one Layers root (prefix `lx-`); never document-wide.
 *                                   Markup must use the full forms (lx-bind:attr, lx-on:event): with a custom
 *                                   prefix Alpine ignores `:` / `@` shorthands, and the app-injected global
 *                                   Alpine would evaluate them on our nodes instead.
 *   .alpine.Alpine                  the pinned Alpine instance (for Alpine.data / Alpine.reactive)
 *   .card.toViewModel(product, opts)  SDK Product -> view-model for snippets/layers-product-card.liquid
 *        opts: { collection?: handle, index?: number, loading?: 'eager'|'lazy', sizes?: string,
 *                reviews?: boolean, finalSale?: boolean, quickShop?: boolean, compact?: boolean }
 *   .card.refresh(root)             re-arm Yotpo, Rivo hearts, quick view after a render (idempotent)
 *   .card.attributes                extra `attributes` every product request should send
 *   .card.imageUrl(src, width)      storefront-domain CDN URL matching Layers' media preloads
 *   .card.imageWidths               [360, 540, 720, 900, 1080]
 *   .card.imageSizes({ layout, columns, mobileColumns, sidebar })  fallback `sizes`
 *   .card.measuredImageSizes(container, fallback)  `${px}px` from a laid-out card/skeleton
 *   .card.mediaPreloads(sizes)      { mediaPreloads: { srcsetSizes, sizes } } for responseOptions
 *   .card.isMerchandise(product)    false for checkout add-ons (shipping protection etc.)
 *   .url.parse(search?)             native URL -> { query, searchId, originalQuery, sort, sortRaw, page, filters }
 *   .url.serialize(state)           state -> '?…' native query string (keeps search_id / original_query)
 *   .url.toFilterGroup(filters)     parsed filters -> SDK filterGroup (OR within a facet, AND across)
 *   .url.translateLegacy()          rewrites Boost pf_* params to native keys (replaceState); returns bool
 *   .search.params(opts)            canonical search params; header prepare and /search first execute
 *        opts: { page?, limit?, filterGroup?, sort?, sizes? }
 *   .search.withSearchId(id)        transformBody forcing search_id onto execute (SDK 3.0.0 drops it)
 *   .search.commit(query, { originalQuery })  prepare (<=250ms) then navigate to /search
 *   .search.url({ query, searchId, originalQuery })  /search URL (original_query only when != query)
 *   .formatMoney(amount)            shop.money_format formatter (major units)
 *   .debounce                       SDK createDebounce
 */

import { createSDK, getSDK, createDebounce } from 'https://cdn.jsdelivr.net/npm/@commerce-blocks/sdk@3.0.0/+esm';
import Alpine from 'https://cdn.jsdelivr.net/npm/alpinejs@3.14.9/dist/module.esm.js';

const cfg = window.LayersConfig || {};
const cardCfg = cfg.card || {};
const i18n = cfg.i18n || {};

/* ---------------------------------------------------------------- Alpine -- */

// An app injects the Alpine 3.14.1 CDN build, which starts over the whole document, and Rivo runs its
// own `rivo-ax-` instance. Ours uses the `lx-` prefix so neither of them evaluates Layers markup, and it
// is never started: each Layers root is initialised on its own.
Alpine.prefix('lx-');

const alpine = {
  Alpine,
  mount(root) {
    if (root && !root._x_dataStack) Alpine.initTree(root);
  },
};

/* ------------------------------------------------------------------- SDK -- */

function readEmbedConfig() {
  const el = document.getElementById('layers-config');
  if (!el) return {};
  try {
    const data = JSON.parse(el.textContent || '{}');
    return { storefrontAccessToken: data.storefrontApiToken, trackingToken: data.apiToken };
  } catch {
    return {};
  }
}

function sessionStore() {
  try {
    const k = '__layers_probe';
    window.sessionStorage.setItem(k, '1');
    window.sessionStorage.removeItem(k);
    return window.sessionStorage;
  } catch {
    return undefined;
  }
}

let sdkInstance;
function sdk() {
  if (sdkInstance !== undefined) return sdkInstance;
  const existing = getSDK();
  if (existing && existing.data) return (sdkInstance = existing.data);
  const { storefrontAccessToken, trackingToken } = readEmbedConfig();
  if (!storefrontAccessToken || !trackingToken) return (sdkInstance = null);
  const storage = sessionStore();
  const { data, error } = createSDK({
    storefrontAccessToken,
    trackingToken,
    // Prepared search ids survive the navigation to /search.
    ...(storage ? { storage } : {}),
    // Short TTL so merchandising changes show within seconds.
    cache: { ttl: 30_000, maxSize: 200 },
    currencyCode: cfg.currencyCode || 'USD',
    formatPrice: formatMoney,
  });
  sdkInstance = error ? null : data;
  return sdkInstance;
}

/* ----------------------------------------------------------- money format -- */

function formatMoney(amount) {
  const value = typeof amount === 'number' ? amount : parseFloat(amount) || 0;
  const format = cfg.moneyFormat || '${{amount}}';
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;
  const match = format.match(placeholder);
  if (!match) return `${format} ${value.toFixed(2)}`.trim();
  const noDecimals = match[1].includes('no_decimals');
  const comma = match[1].includes('comma_separator');
  let [whole, cents] = value.toFixed(noDecimals ? 0 : 2).split('.');
  whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, comma ? '.' : ',');
  const rendered = cents ? `${whole}${comma ? ',' : '.'}${cents}` : whole;
  return format.replace(placeholder, rendered);
}

/* ------------------------------------------------------------ URL contract -- */

const keys = { query: 'q', page: 'page', sort: 'sort_by', searchId: 'search_id', originalQuery: 'original_query', ...(cfg.urlKeys || {}) };
const facetsByKey = cfg.facets || {};

const nativeKeyByCode = Object.entries(facetsByKey).reduce((acc, [key, f]) => {
  if (f.kind === 'range_min') acc[`${f.code}::min`] = key;
  else if (f.kind === 'range_max') acc[`${f.code}::max`] = key;
  else acc[f.code] = key;
  return acc;
}, {});

/** Native collection/search URL -> Layers state. */
function parseLayersStateFromURL(search) {
  const params = new URLSearchParams(search ?? window.location.search);
  const filters = {};
  for (const [key, f] of Object.entries(facetsByKey)) {
    if (f.kind === 'value') {
      const values = params
        .getAll(key)
        .flatMap((v) => v.split(','))
        .filter(Boolean)
        .map((v) => (f.values && f.values[v] != null ? f.values[v] : v));
      if (values.length) (filters[f.code] ||= { code: f.code, values: [] }).values.push(...values);
    } else {
      const raw = params.get(key);
      if (raw == null || raw === '') continue;
      const range = (filters[f.code] ||= { code: f.code });
      range[f.kind === 'range_min' ? 'gte' : 'lte'] = parseFloat(raw);
    }
  }
  const sortRaw = params.get(keys.sort) || undefined;
  const sorts = cfg.sorts || {};
  return {
    query: params.get(keys.query) || undefined,
    searchId: params.get(keys.searchId) || undefined,
    originalQuery: params.get(keys.originalQuery) || undefined,
    // Unknown native sorts fall back to the default (null), as "manual" does.
    sort: sortRaw && sorts[sortRaw] != null ? sorts[sortRaw] : sortRaw && Object.values(sorts).includes(sortRaw) ? sortRaw : undefined,
    sortRaw,
    page: Math.max(1, parseInt(params.get(keys.page) || '1', 10) || 1),
    filters: Object.values(filters),
  };
}

/** Layers state -> native query string. search_id / original_query ride along until the query changes. */
function serializeLayersStateToURL(state = {}) {
  const params = new URLSearchParams();
  if (state.query) params.set(keys.query, state.query);
  if (state.searchId) params.set(keys.searchId, state.searchId);
  if (state.originalQuery && state.originalQuery !== state.query) params.set(keys.originalQuery, state.originalQuery);
  if (state.sortRaw) params.set(keys.sort, state.sortRaw);
  for (const f of state.filters || []) {
    if (f.values && f.values.length) {
      const key = nativeKeyByCode[f.code];
      if (!key) continue;
      const map = (facetsByKey[key] || {}).values || {};
      const toUrl = (v) => Object.keys(map).find((k) => map[k] === v) ?? v;
      // One key per value, as Shopify's native filter links do.
      f.values.forEach((v) => params.append(key, toUrl(v)));
    } else {
      if (f.gte != null && nativeKeyByCode[`${f.code}::min`]) params.set(nativeKeyByCode[`${f.code}::min`], String(f.gte));
      if (f.lte != null && nativeKeyByCode[`${f.code}::max`]) params.set(nativeKeyByCode[`${f.code}::max`], String(f.lte));
    }
  }
  if (state.page && state.page > 1) params.set(keys.page, String(state.page));
  const s = params.toString();
  return s ? `?${s}` : '';
}

/** Parsed filters -> SDK filterGroup: values OR within a facet, facets AND across. */
function toFilterGroup(filters = []) {
  const expressions = [];
  for (const f of filters) {
    if (f.values && f.values.length) {
      expressions.push({ property: f.code, operator: 'in', values: f.values });
    } else if (f.gte != null && f.lte != null) {
      // One BETWEEN: separate gte + lte on the same property skews the other facets' counts.
      expressions.push({ property: f.code, operator: 'between', values: [f.gte, f.lte] });
    } else if (f.gte != null) {
      expressions.push({ property: f.code, operator: 'gte', values: [f.gte] });
    } else if (f.lte != null) {
      expressions.push({ property: f.code, operator: 'lte', values: [f.lte] });
    }
  }
  return expressions.length ? { conditional: 'AND', expressions } : undefined;
}

/** Boost pf_* inbound links -> native keys (config legacyParams). Returns true when the URL changed. */
function translateLegacyParams() {
  const map = cfg.legacyParams || {};
  if (!Object.keys(map).length) return false;
  const params = new URLSearchParams(window.location.search);
  let changed = false;
  for (const [legacy, native] of Object.entries(map)) {
    if (!params.has(legacy)) continue;
    // Boost comma-joins values; Shopify repeats the key per value.
    params.getAll(legacy).flatMap((v) => v.split(',')).map((v) => v.trim()).filter(Boolean).forEach((v) => params.append(native, v));
    params.delete(legacy);
    changed = true;
  }
  if (changed) {
    const qs = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`);
  }
  return changed;
}

/* ---------------------------------------------------------------- search -- */

const PREPARE_WAIT_MS = 250;
const CARD_ATTRIBUTES = ['tags', 'variants[].title', 'metafields.custom.swatch_image_position'];

/**
 * Canonical first-page search params. The header prepare() and /search's first execute() must send
 * identical params, or the SDK's prepare-store key misses and the prepared search_id isn't reused.
 * responseOptions sits outside that key, so per-surface image preloads are safe to add.
 */
function searchParams({ page = 1, limit, filterGroup, sort, sizes } = {}) {
  const facetCodes = [...new Set(Object.values(facetsByKey).map((f) => f.code))];
  const params = {
    pagination: { page, limit: limit || cfg.pageSize || 28 },
    facets: facetCodes,
    retrieveFacetCount: true,
    includeFacetRanges: true,
    attributes: CARD_ATTRIBUTES,
  };
  if (filterGroup) params.filterGroup = filterGroup;
  if (sort) params.sort = sort;
  if (sizes) params.responseOptions = mediaPreloads(sizes);
  return params;
}

/** SDK 3.0.0's search controller drops a caller-supplied searchId; force the URL's onto the body. */
function withSearchId(searchId) {
  return searchId ? (body) => ({ ...body, search_id: searchId }) : undefined;
}

function searchUrl({ query, searchId, originalQuery }) {
  return `${cfg.searchUrl || '/search'}${serializeLayersStateToURL({ query, searchId, originalQuery })}`;
}

/**
 * Committed search (Enter, submit, suggestion, see-all, trending chip): prepare, wait at most
 * PREPARE_WAIT_MS, then go to /search with search_id (+ original_query when the shopper typed
 * something else). A slow or failed prepare never blocks navigation.
 */
async function commitSearch(query, { originalQuery } = {}) {
  const q = (query || '').trim();
  if (!q) return;
  let searchId;
  const layers = sdk();
  if (layers) {
    const prepare = layers
      .search({ query: q, ...searchParams() })
      .prepare()
      .then((res) => res && res.data && res.data.search_id)
      .catch(() => undefined);
    const timeout = new Promise((resolve) => setTimeout(resolve, PREPARE_WAIT_MS));
    searchId = await Promise.race([prepare, timeout]);
  }
  const typed = (originalQuery || '').trim();
  window.location.assign(searchUrl({ query: q, searchId, originalQuery: typed && typed !== q ? typed : undefined }));
}

/* ----------------------------------------------------------------- images -- */

/*
 * Image URLs match Layers' media preloads byte for byte. Search/browse/blocks responses carry a
 * `Link: rel=preload` header for the first results' images on the storefront domain
 * ({origin}/cdn/shop/files/x.jpg?width=N). Cards build the same URLs from the same widths, and every
 * request sends those widths plus the surface's `sizes`, so each image downloads once.
 */
const CARD_IMAGE_WIDTHS = [360, 540, 720, 900, 1080];
const SHOPIFY_CDN_PATH = /^\/s\/files\/(?:\d+\/)+((?:files|products|collections)\/.+)$/;

function imageUrl(src, width) {
  if (!src) return '';
  if (src.includes('{width}')) return src.replace('{width}', String(width));
  try {
    const url = new URL(src, window.location.origin);
    const cdnPath = url.hostname === 'cdn.shopify.com' && url.pathname.match(SHOPIFY_CDN_PATH);
    if (cdnPath) return `${window.location.origin}/cdn/shop/${cdnPath[1]}?width=${width}`;
    url.searchParams.delete('v');
    url.searchParams.set('width', String(width));
    return url.toString();
  } catch {
    return src;
  }
}

function imageSrcset(src) {
  return CARD_IMAGE_WIDTHS.map((w) => `${imageUrl(src, w)} ${w}w`).join(', ');
}

/**
 * Fallback `sizes` (Impulse small breakpoint 768px). Layers' sizes validator rejects nested
 * parentheses, so no calc((a - b) / n).
 *   grid:     collection/search grid; `sidebar` = filter column (one fifth) beside it on desktop
 *   carousel: recommendation rows, `columns` cards per view
 */
function cardImageSizes({ layout = 'grid', columns = 4, mobileColumns = 2, sidebar = true } = {}) {
  const mobile = `${Math.ceil(100 / mobileColumns)}vw`;
  if (layout === 'carousel') return `(min-width: 769px) ${Math.ceil(100 / columns)}vw, ${Math.ceil(100 / Math.min(columns, 2.3))}vw`;
  const desktop = sidebar ? Math.ceil(80 / columns) : Math.ceil(100 / columns);
  return `(min-width: 769px) ${desktop}vw, ${mobile}`;
}

/** `${px}px` from the rendered width of a card (or skeleton card) already laid out in `container`. */
function measuredImageSizes(container, fallback) {
  const card = [...((container && container.querySelectorAll('.grid-product__image-mask')) || [])].find(
    (el) => el.getBoundingClientRect().width > 0,
  );
  const width = card ? Math.ceil(card.getBoundingClientRect().width) : 0;
  return width ? `${width}px` : fallback;
}

function mediaPreloads(sizes, widths = CARD_IMAGE_WIDTHS) {
  return { mediaPreloads: { srcsetSizes: widths, sizes } };
}

/* ------------------------------------------------------------- card model -- */

function isMerchandise(product) {
  return !/order processing|package protection|shipping protection|route protection|worry-free purchase/i.test((product && product.title) || '');
}

function metafield(product, dotted) {
  return dotted.split('.').reduce((o, k) => (o == null ? undefined : o[k]), product.metafields || {});
}

function customLabel(product) {
  const meta = metafield(product, cardCfg.labelMetafield || 'theme.label');
  if (typeof meta === 'string' && meta.trim()) return meta.trim();
  const prefix = cardCfg.labelTagPrefix || '_label_';
  const tag = (product.tags || []).filter((t) => t.startsWith(prefix)).pop();
  return tag ? tag.slice(prefix.length) : '';
}

function colorwayKey(tags) {
  const found = [...new Set((tags || []).map((t) => String(t).trim().toLowerCase()).filter((t) => t.startsWith('sku_') && t.length > 4))];
  return found.length === 1 ? found[0] : '';
}

const COLOR_TRIGGER = /colou?r/i;

/** Impulse colour swatches (cards without a colorway row): one per Color value with a variant URL. */
function colorSwatches(product, baseUrl) {
  if (!cardCfg.swatches) return [];
  const option = (product.options || []).find((o) => COLOR_TRIGGER.test(o.name || ''));
  if (!option) return [];
  const seen = new Map();
  for (const v of product.variants || []) {
    const value = (v.selectedOptions || []).find((o) => o.name === option.name)?.value;
    if (!value || seen.has(value)) continue;
    const handle = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    seen.set(value, {
      value,
      handle,
      color: value.split(' ').pop().toLowerCase(),
      url: `${baseUrl}?variant=${v.numericId}`,
    });
  }
  return seen.size > 1 ? [...seen.values()] : [];
}

/**
 * SDK Product -> view-model for snippets/layers-product-card.liquid. Mirrors the Liquid logic in
 * snippets/product-grid-item.liquid (badge precedence, price, save) so both cards read the same.
 */
function toCardViewModel(product, opts = {}) {
  const variants = product.variants || [];
  const tags = product.tags || [];
  const amounts = variants.map((v) => v.price && v.price.amount).filter((n) => typeof n === 'number');
  const compares = variants.map((v) => v.compareAtPrice && v.compareAtPrice.amount).filter((n) => typeof n === 'number' && n > 0);
  const priceMin = amounts.length ? Math.min(...amounts) : (product.price && product.price.amount) || 0;
  const priceMax = amounts.length ? Math.max(...amounts) : priceMin;
  const compareMin = compares.length ? Math.min(...compares) : product.compareAtPrice ? product.compareAtPrice.amount : 0;
  const onSale = compareMin > priceMin;
  // Sold out only when every variant is: the product flag can follow the first variant alone.
  const available = variants.length ? variants.some((v) => v.availableForSale !== false) : product.availableForSale !== false;

  const label = customLabel(product);
  const finalSale = !!opts.finalSale && available && tags.includes(cardCfg.finalSaleTag || 'Final_Sale');
  let badge = null;
  if (label) badge = { type: 'custom', text: label };
  else if (finalSale) badge = { type: 'final-sale', text: i18n.finalSale };
  else if (!available) badge = { type: 'sold-out', text: i18n.soldOut };
  else if (onSale) badge = { type: 'sale', text: i18n.sale };

  let savings = '';
  if (onSale && cardCfg.saveAmount) {
    const saved = cardCfg.saveType === 'dollar'
      ? formatMoney(compareMin - priceMin)
      : `${Math.round(((compareMin - priceMin) * 100) / compareMin)}%`;
    savings = (i18n.saveHtml || 'Save ___amount___').replace('___amount___', saved);
  }
  const current = formatMoney(priceMin);

  const baseUrl = opts.collection ? `/collections/${opts.collection}/products/${product.handle}` : `/products/${product.handle}`;
  // Layers preloads featured_media, so it leads; the rest keep their order (hover = next image).
  const mediaPath = (u) => (u || '').split('?')[0];
  const featured = product.featuredMedia && product.featuredMedia.url ? product.featuredMedia : null;
  const gallery = product.images || [];
  const ordered = featured && gallery.length && mediaPath(gallery[0].url) !== mediaPath(featured.url)
    ? [featured, ...gallery.filter((img) => mediaPath(img.url) !== mediaPath(featured.url))]
    : gallery.length ? gallery : featured ? [featured] : [];
  const images = ordered.map((img, i) => ({
    key: `${product.id}:${i}`,
    src: imageUrl(img.url, 720),
    srcset: imageSrcset(img.url),
    alt: img.alt || product.title,
    width: img.width || 2048,
    height: img.height || 3072,
  }));

  const colorwaysOn = !!(cardCfg.colorways && cardCfg.colorways.enabled) && !opts.compact;
  // Flow tags every product SIBLINGS_ASSIGNED / SIBLINGS_NONE; only grouped styles fetch a row.
  const hasSiblings = colorwaysOn && tags.includes('SIBLINGS_ASSIGNED');
  const loading = opts.loading === 'eager' ? 'eager' : 'lazy';

  return {
    id: product.id,
    numericId: product.numericId,
    handle: product.handle,
    title: product.title,
    vendor: cardCfg.vendor ? product.vendor : '',
    url: baseUrl,
    available,
    images,
    image: images[0] || null,
    secondImage: cardCfg.hoverImage && images.length > 1 ? images[1] : null,
    loading,
    fetchpriority: loading === 'eager' ? 'high' : 'auto',
    sizes: opts.sizes || cardImageSizes(),
    index: opts.index ?? 0,
    badge,
    price: {
      onSale,
      original: onSale ? formatMoney(compareMin) : '',
      current,
      varies: priceMin < priceMax,
      fromHtml: (i18n.fromHtml || 'from ___price___').replace('___price___', current),
      savings,
    },
    customText: (cardCfg.customText || []).filter((c) => tags.includes(c.tag)),
    reviews: !!opts.reviews,
    quickShop: (opts.quickShop ?? cardCfg.quickShop) && !opts.compact,
    quickShopText: cardCfg.quickShopText,
    compact: !!opts.compact,
    // hydrate: fetch the sibling row; otherwise an empty spacer row when the theme reserves one.
    colorways: colorwaysOn && (hasSiblings || cardCfg.colorways.reserveRow)
      ? { position: cardCfg.colorways.position || 'end', key: colorwayKey(tags), hydrate: hasSiblings }
      : null,
    colorSwatches: hasSiblings || opts.compact ? [] : colorSwatches(product, baseUrl),
  };
}

/* ------------------------------------------------------------ card refresh -- */

/**
 * Re-arm third-party card integrations after a client render. Safe to call after every render:
 * each card is wired once (data-layers-armed), and Yotpo/Rivo re-scans are idempotent.
 *   Yotpo v3:  yotpoWidgetsContainer.initWidgets() picks up new .yotpo-widget-instance nodes
 *   Rivo:      RivoProfileAlpine.initTree(heart) + initializeFavoriteButtons()
 *   Quick view: preload the ?view=modal product on first hover/focus (theme.preloadProductModal),
 *              as theme.initQuickShop does; called per card, so earlier cards never double-bind
 */
let yotpoTimer;
function refreshCardIntegrations(root) {
  const scope = root || document;
  const cards = [...scope.querySelectorAll('.grid-product[data-layers-card]:not([data-layers-armed])')];
  cards.forEach((card) => {
    card.setAttribute('data-layers-armed', '');
    const heart = card.querySelector('.favorite-button-wrapper');
    if (heart && window.RivoProfileAlpine) {
      try { window.RivoProfileAlpine.initTree(heart); } catch { /* Rivo mid-load */ }
    }
    armQuickShop(card);
  });
  if (cards.length) {
    try {
      const account = document.getElementById('rivo-account-slideout');
      if (window.RivoProfileAlpine && account) window.RivoProfileAlpine.$data(account).initializeFavoriteButtons();
    } catch { /* Rivo absent */ }
    clearTimeout(yotpoTimer);
    yotpoTimer = setTimeout(() => {
      try {
        if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') window.yotpoWidgetsContainer.initWidgets();
        else if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') window.yotpo.refreshWidgets();
      } catch { /* Yotpo mid-load */ }
    }, 50);
  }
  scope.dispatchEvent(new CustomEvent('layers:cards:rendered', { bubbles: true, detail: { root: scope, count: cards.length } }));
}

function armQuickShop(card) {
  const theme = window.theme;
  const btn = card.querySelector('.quick-product__btn');
  if (!btn || !theme || !theme.settings || !theme.settings.quickView || typeof theme.preloadProductModal !== 'function') return;
  const preload = () => {
    if (theme.config && theme.config.bpSmall) return;
    card.removeEventListener('mouseover', preload);
    card.removeEventListener('focusin', preload);
    theme.preloadProductModal(card.dataset.productHandle, card.dataset.productId, btn);
  };
  card.addEventListener('mouseover', preload);
  card.addEventListener('focusin', preload);
}

/* ---------------------------------------------------------------- expose -- */

const LayersTheme = {
  sdk,
  config: cfg,
  alpine,
  formatMoney,
  debounce: createDebounce,
  card: {
    toViewModel: toCardViewModel,
    refresh: refreshCardIntegrations,
    attributes: CARD_ATTRIBUTES,
    imageUrl,
    imageWidths: CARD_IMAGE_WIDTHS,
    imageSizes: cardImageSizes,
    measuredImageSizes,
    mediaPreloads,
    isMerchandise,
  },
  url: {
    parse: parseLayersStateFromURL,
    serialize: serializeLayersStateToURL,
    toFilterGroup,
    translateLegacy: translateLegacyParams,
  },
  search: { params: searchParams, withSearchId, commit: commitSearch, url: searchUrl },
};
LayersTheme.ready = Promise.resolve(LayersTheme);
window.LayersTheme = LayersTheme;
document.dispatchEvent(new CustomEvent('layers:ready', { detail: LayersTheme }));
